import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
  alpha,
  ListItemButton,
  Popover,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Phone as CallIcon,
  CallEnd as HangUpIcon,
  Backspace as BackspaceIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  PhoneAndroid as PhoneAndroidIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useWebPhoneContextV3 } from '../../contexts/WebPhoneContextV3';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const WEBPHONE_VERSION = 'VM3.00.01';

interface CallHistoryItem {
  id: number;
  remote_number: string;
  direction: 'inbound' | 'outbound' | 'internal';
  start_time: string;
  duration: number;
  duration_formatted: string;
  disposition: string;
  caller_id_name?: string;
}

// Styled components for iPhone-like appearance
const PhoneContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '100vw',
  backgroundColor: '#000',
  color: '#fff',
  overflow: 'hidden',
});

const StatusBar = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 16px',
  backgroundColor: '#000',
  minHeight: '44px',
});

const NumberDisplay = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: '60px',
  fontSize: '32px',
  fontWeight: '300',
  letterSpacing: '1px',
  padding: '8px 20px',
  textAlign: 'center',
  color: '#fff',
});

const DialpadContainer = styled(Box)({
  flex: 1,
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '12px',
  padding: '12px',
  maxWidth: '400px',
  margin: '0 auto',
  width: '100%',
});

const DialpadButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  aspectRatio: '1',
  borderRadius: '50%',
  backgroundColor: alpha('#fff', 0.12),
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'all 0.15s ease',
  '&:active': {
    backgroundColor: alpha('#fff', 0.25),
    transform: 'scale(0.95)',
  },
}));

const ActionButton = styled(Box)<{ color?: string }>(({ color }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  aspectRatio: '1',
  borderRadius: '50%',
  backgroundColor: color || alpha('#fff', 0.12),
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

const CallControls = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '12px 16px 16px 16px',
  gap: '16px',
  maxWidth: '400px',
  margin: '0 auto',
  width: '100%',
});

const WebPhoneStandaloneMobileV3: React.FC = () => {
  const {
    connectionStatus,
    callStatus,
    currentCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    addDigit,
    deleteDigit,
    clearNumber,
    connect,
  } = useWebPhoneContextV3();

  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const numberDisplayRef = useRef<HTMLDivElement>(null);

  // Check if audio permission needs to be requested
  useEffect(() => {
    const audioEnabled = localStorage.getItem('webphone_audio_enabled') === 'true';
    if (!audioEnabled && callStatus === 'in-call') {
      setShowAudioPrompt(true);
    } else {
      setShowAudioPrompt(false);
    }
  }, [callStatus]);

  // Handle enable audio
  const handleEnableAudio = () => {
    localStorage.setItem('webphone_audio_enabled', 'true');
    setShowAudioPrompt(false);
    // Trigger audio playback
    const audioElement = document.getElementById('webphone-remote-audio-v3') as HTMLAudioElement;
    if (audioElement) {
      audioElement.muted = false;
      audioElement.play().catch(e => console.error('Failed to play audio:', e));
    }
  };

  // Auto-connect on mount
  useEffect(() => {
    if (connectionStatus === 'disconnected') {
      connect();
    }
  }, []);

  // Fetch call history
  const fetchCallHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/endpoints/user-call-history-simple', {
        params: {
          per_page: 30,
          page: 1,
        }
      });

      if (response.data.success) {
        setCallHistory(response.data.data.calls);
      }
    } catch (error) {
      console.error('Failed to fetch call history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle hover over number display (open history)
  const handleHistoryOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (callStatus === 'idle') {
      setHistoryAnchorEl(event.currentTarget);
      if (callHistory.length === 0) {
        fetchCallHistory();
      }
    }
  };

  // Handle close history
  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
  };

  // Handle select number from history
  const handleSelectNumber = (number: string) => {
    clearNumber();
    number.split('').forEach(digit => addDigit(digit));
    handleHistoryClose();
  };

  // Keyboard input support
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only handle keyboard when idle or in call (for DTMF)
      if (callStatus !== 'idle' && callStatus !== 'in-call') return;

      // Handle number keys
      if (/^[0-9*#]$/.test(event.key)) {
        event.preventDefault();
        addDigit(event.key);
      }
      // Handle backspace
      else if (event.key === 'Backspace' && callStatus === 'idle') {
        event.preventDefault();
        deleteDigit();
      }
      // Handle enter to make call
      else if (event.key === 'Enter' && callStatus === 'idle' && dialedNumber) {
        event.preventDefault();
        makeCall(dialedNumber);
      }
    };

    // Handle paste events
    const handlePaste = (event: ClipboardEvent) => {
      if (callStatus !== 'idle') return;

      const pastedText = event.clipboardData?.getData('text');
      if (pastedText) {
        event.preventDefault();
        // Extract only numbers, *, and # from pasted text
        const cleanedNumber = pastedText.replace(/[^0-9*#]/g, '');
        if (cleanedNumber) {
          clearNumber();
          cleanedNumber.split('').forEach(digit => addDigit(digit));
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    document.addEventListener('paste', handlePaste);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      document.removeEventListener('paste', handlePaste);
    };
  }, [callStatus, dialedNumber, addDigit, deleteDigit, clearNumber, makeCall]);

  // Format call duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status display
  const getStatusText = () => {
    if (callStatus === 'calling') return 'Calling...';
    if (callStatus === 'ringing' && currentCall?.direction === 'incoming') return 'Incoming Call';
    if (callStatus === 'ringing') return 'Ringing...';
    if (callStatus === 'in-call' && currentCall) {
      return formatDuration(currentCall.duration);
    }
    if (connectionStatus === 'connecting') return 'Connecting...';
    if (connectionStatus === 'connected') return 'Connected';
    if (connectionStatus === 'registered') return 'Ready';
    if (connectionStatus === 'error') return 'Connection Error';
    return 'Disconnected';
  };

  // Get display number
  const getDisplayNumber = () => {
    if (currentCall && callStatus !== 'idle') {
      return currentCall.remoteNumber;
    }
    return dialedNumber || '';
  };

  // Dialpad keys
  const dialpadKeys = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '+' },
    { digit: '#', letters: '' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    navigate('/login');
  };

  const handleOpenWebPhone = (version: string) => {
    const url = `/webphone-popup-${version}`;
    const windowName = `webphone_${version}`;
    const features = 'width=400,height=600,resizable=yes,scrollbars=no';
    window.open(url, windowName, features);
    setDrawerOpen(false);
  };

  return (
    <PhoneContainer>
      {/* Status Bar with Hamburger Menu */}
      <StatusBar>
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{ color: '#fff', padding: '8px' }}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
          {getStatusText()}
        </Typography>
        <Box sx={{ width: 40 }} /> {/* Spacer for centering */}
      </StatusBar>

      {/* Error Message */}
      {errorMessage && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'error.main' }}>
          <Typography variant="caption">{errorMessage}</Typography>
        </Box>
      )}

      {/* Audio Enable Prompt */}
      {showAudioPrompt && (
        <Box sx={{ px: 2, py: 1, bgcolor: '#FFA500', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: '#000' }}>
            Tap to enable audio
          </Typography>
          <IconButton
            onClick={handleEnableAudio}
            sx={{
              bgcolor: '#fff',
              color: '#000',
              '&:hover': { bgcolor: alpha('#fff', 0.9) },
            }}
          >
            <MicIcon />
          </IconButton>
        </Box>
      )}

      {/* Number Display with Backspace and History */}
      <NumberDisplay
        ref={numberDisplayRef}
        onMouseEnter={handleHistoryOpen}
        sx={{
          cursor: callStatus === 'idle' ? 'pointer' : 'default',
          transition: 'background-color 0.2s',
          '&:hover': callStatus === 'idle' ? {
            backgroundColor: alpha('#fff', 0.05),
          } : {},
        }}
      >
        <Box sx={{ width: 40 }} /> {/* Spacer for balance */}
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          {getDisplayNumber() || (
            <Typography variant="h4" sx={{ color: alpha('#fff', 0.3) }}>
              Enter number
            </Typography>
          )}
        </Box>
        {dialedNumber && callStatus === 'idle' ? (
          <IconButton
            onClick={deleteDigit}
            sx={{
              color: '#fff',
              padding: '8px',
            }}
          >
            <BackspaceIcon />
          </IconButton>
        ) : (
          <Box sx={{ width: 40 }} /> /* Spacer when no backspace */
        )}
      </NumberDisplay>

      {/* Call History Popover */}
      <Popover
        open={Boolean(historyAnchorEl)}
        anchorEl={historyAnchorEl}
        onClose={handleHistoryClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#1c1c1e',
            color: '#fff',
            maxWidth: '90vw',
            width: '100%',
            maxHeight: '50vh',
            marginTop: '8px',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HistoryIcon sx={{ mr: 1, color: alpha('#fff', 0.6) }} />
            <Typography variant="h6">Recent Calls</Typography>
          </Box>

          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={32} sx={{ color: '#fff' }} />
            </Box>
          ) : callHistory.length === 0 ? (
            <Typography variant="body2" sx={{ color: alpha('#fff', 0.5), textAlign: 'center', p: 2 }}>
              No recent calls
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {callHistory.map((call) => (
                <ListItemButton
                  key={call.id}
                  onClick={() => handleSelectNumber(call.remote_number)}
                  sx={{
                    borderRadius: '8px',
                    mb: 0.5,
                    '&:hover': {
                      backgroundColor: alpha('#fff', 0.1),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {call.direction === 'outbound' ? (
                      <CallMadeIcon sx={{ color: '#34C759' }} />
                    ) : (
                      <CallReceivedIcon sx={{ color: '#007AFF' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={call.remote_number}
                    secondary={
                      <Box component="span" sx={{ color: alpha('#fff', 0.5) }}>
                        {call.caller_id_name && `${call.caller_id_name} • `}
                        {call.duration_formatted} • {new Date(call.start_time).toLocaleDateString()}
                      </Box>
                    }
                    primaryTypographyProps={{
                      sx: { color: '#fff', fontWeight: 500 },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Popover>

      {/* Dialpad - Show only when idle or in call (for DTMF) */}
      {(callStatus === 'idle' || callStatus === 'in-call') && (
        <DialpadContainer>
          {dialpadKeys.map((key) => (
            <DialpadButton
              key={key.digit}
              onClick={() => addDigit(key.digit)}
            >
              <Typography variant="h4" sx={{ fontWeight: 300 }}>
                {key.digit}
              </Typography>
              {key.letters && (
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontSize: '10px', mt: -0.5 }}>
                  {key.letters}
                </Typography>
              )}
            </DialpadButton>
          ))}
        </DialpadContainer>
      )}

      {/* Call Controls */}
      <CallControls>
        {callStatus === 'idle' && (
          <ActionButton
            color="#34C759"
            onClick={() => makeCall(dialedNumber)}
            sx={{ width: 72, height: 72 }}
          >
            <CallIcon sx={{ fontSize: 32 }} />
          </ActionButton>
        )}

        {callStatus === 'calling' && (
          <>
            <Box sx={{ flex: 1 }} />
            <ActionButton
              color="#FF3B30"
              onClick={hangUp}
              sx={{ width: 72, height: 72 }}
            >
              <HangUpIcon sx={{ fontSize: 32 }} />
            </ActionButton>
            <Box sx={{ flex: 1 }} />
          </>
        )}

        {callStatus === 'ringing' && currentCall?.direction === 'incoming' && (
          <>
            <ActionButton
              color="#FF3B30"
              onClick={hangUp}
              sx={{ width: 72, height: 72 }}
            >
              <HangUpIcon sx={{ fontSize: 32 }} />
            </ActionButton>
            <Box sx={{ flex: 1 }} />
            <ActionButton
              color="#34C759"
              onClick={answerCall}
              sx={{ width: 72, height: 72 }}
            >
              <CallIcon sx={{ fontSize: 32 }} />
            </ActionButton>
          </>
        )}

        {callStatus === 'in-call' && (
          <>
            <ActionButton
              onClick={toggleMute}
              sx={{
                width: 64,
                height: 64,
                bgcolor: isMuted ? '#fff' : alpha('#fff', 0.12),
                color: isMuted ? '#000' : '#fff',
              }}
            >
              {isMuted ? <MicOffIcon /> : <MicIcon />}
            </ActionButton>

            <ActionButton
              color="#FF3B30"
              onClick={hangUp}
              sx={{ width: 72, height: 72 }}
            >
              <HangUpIcon sx={{ fontSize: 32 }} />
            </ActionButton>

            <ActionButton
              onClick={toggleHold}
              sx={{
                width: 64,
                height: 64,
                bgcolor: isOnHold ? '#fff' : alpha('#fff', 0.12),
                color: isOnHold ? '#000' : '#fff',
              }}
            >
              {isOnHold ? <PlayIcon /> : <PauseIcon />}
            </ActionButton>
          </>
        )}
      </CallControls>

      {/* Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: '#1c1c1e',
            color: '#fff',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />
        <List>
          <ListItem
            button
            onClick={() => {
              navigate('/dashboard');
              setDrawerOpen(false);
            }}
          >
            <ListItemIcon sx={{ color: '#fff', minWidth: 40 }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItem>
          <ListItem
            button
            onClick={() => {
              navigate('/settings');
              setDrawerOpen(false);
            }}
          >
            <ListItemIcon sx={{ color: '#fff', minWidth: 40 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItem>
        </List>
        <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />
        <List>
          <ListItem button onClick={handleLogout}>
            <ListItemIcon sx={{ color: '#ff3b30', minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ color: '#ff3b30' }} />
          </ListItem>
        </List>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.3) }}>
            WebPhone Mobile VM3
          </Typography>
          <br />
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.3) }}>
            v{WEBPHONE_VERSION}
          </Typography>
        </Box>
      </Drawer>
    </PhoneContainer>
  );
};

export default WebPhoneStandaloneMobileV3;
